import Box from '@mui/material/Box';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import IconMeta from '../../assets/icon-meta.svg?react';
import IconTikTok from '../../assets/icon-tiktok.svg?react';
import { Accordion, AccordionDetails, AccordionSummary, List, styled, Typography } from '@mui/material';
import CabinetMenuLink from './CabinetMenuLink';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useCabinetStore } from 'features/cabinet';
import { useCabinetMenuAccess } from 'features/login';

export const MenuAccordion = styled(Accordion)(({ theme: t }) => ({
  '&': {
    alignItems: 'start',
    justifyContent: 'start',
    width: '100%',
    gap: t.spacing(1),
  },
  '&.Mui-expanded': { margin: 0, minHeight: 24 },
  padding: 0,
  boxShadow: 'none',
  border: 'none',
  '&:before': { display: 'none' },
}));

export const MenuAccordionSummary = styled(AccordionSummary)({
  '&': { display: 'flex', minHeight: '0', height: 32, padding: 0 },
  '&.Mui-expanded': { margin: 0, minHeight: 32 },
  '& .MuiAccordionSummary-content': {
    gap: 8,
    margin: 0,
  },
});

export const MenuAccordionDetails = styled(AccordionDetails)(
  ({ theme: t }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'start',
    justifyContent: 'start',
    padding: '0',
    boxShadow: 'none',
    border: 'none',
    gap: t.spacing(1),
  }),
);

const CabinetMenu = memo(() => {
  const { canAccessMenu } = useCabinetMenuAccess();
  const cabinetMenuRef = useRef([
    {
      network: 'Facebook',
      icon: IconMeta,
      menuItems: [
        {
          label: 'Ads history',
          path: '/ads-history',
        },
        {
          label: 'Flows',
          path: '/flows',
        },
        {
          label: 'Creative texts',
          path: '/creative-texts',
        },
        {
          label: 'Accounts',
          path: '/accounts',
        },
        {
          label: 'Facebook pages',
          path: '/facebook-pages',
        },
        {
          label: 'Go to test',
          path: '/go-to-test',
        },
      ],
    },
    {
      network: 'TikTok',
      icon: IconTikTok,
      menuItems: [
        {
          label: 'Ads History',
          path: '/ads-history',
        },
        {
          label: 'Creative Texts',
          path: '/creative-texts',
        },
        {
          label: 'Flows',
          path: '/flows',
        },
        {
          label: 'Go To Test',
          path: '/go-to-test',
        },
      ],
    },
  ]);
  const cabinetBottomMenuRef = useRef([
    {
      label: 'Users',
      path: '/users',
    },
  ]);
  const { expandedMenu } = useCabinetStore();
  const location = useLocation();
  const [expandedByNetwork, setExpandedByNetwork] = useState<
    Record<string, boolean>
  >(() => {
    const initialState: Record<string, boolean> = {};
    cabinetMenuRef.current.forEach(({ network }) => {
      const normalized = network.toLowerCase();
      initialState[network] = location.pathname.includes(
        `/cabinet/${normalized}`,
      );
    });
    return initialState;
  });

  const handleExpansion = useCallback(
    (network: string) => {
      setExpandedByNetwork((prev) => ({ ...prev, [network]: !prev[network] }));
    },
    [setExpandedByNetwork],
  );

  useEffect(() => {
    setExpandedByNetwork(prev => ({
      ...prev,
      'Facebook': location.pathname.includes('/cabinet/facebook/'),
      'TikTok': location.pathname.includes('/cabinet/tiktok/'),
    }));
  }, [location.pathname]);

  return (
    <Box
      className={expandedMenu ? 'hidden' : ''}
      sx={{
        width: 180,
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'white',
        transition: 'all 0.2s',
        opacity: 1,
        '&.hidden': {
          opacity: 0,
          width: '0px',
        },
      }}
    >
      <Box
        className={expandedMenu ? 'hidden' : ''}
        sx={{
          position: 'absolute',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          maxHeight: '100%',
          height: '100%',
          width: 180,
          px: 1.5,
          pb: 1.5,
          pt: 2,
          gap: 2,
          transform: 'translateX(0px)',
          transition: 'transform 0.4s',
          '&.hidden': {
            transform: 'translateX(10px)',
          },
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          <List sx={{ width: '100%', p: 0, m: 0 }}>
            <CabinetMenuLink
              to={`/cabinet/overview`}
              title={'Overview'}
            />
          </List>
          {cabinetMenuRef.current.map(
            ({ network, icon: IconItem, menuItems }) => {
              const isExpanded = Boolean(expandedByNetwork[network]);
              return (
                <MenuAccordion
                  key={`cabinetMenu.item.${network}`}
                  expanded={isExpanded}
                  onChange={() => {
                    handleExpansion(network);
                  }}
                >
                  <MenuAccordionSummary
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      display: 'flex',
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      <IconItem />
                      <Typography
                        sx={(t) => ({
                          fontSize: t.typography.subtitle2.fontSize,
                          fontWeight: 500,
                          letterSpacing: 0.1,
                          p: 0,
                          m: 0,
                        })}
                      >
                        {network}
                      </Typography>
                    </Box>
                    <ExpandMoreOutlined
                      sx={{
                        transition: 'transform 0.2s',
                        transform: `rotate(${isExpanded ? 180 : 0}deg)`,
                      }}
                    />
                  </MenuAccordionSummary>
                  <MenuAccordionDetails>
                    <List sx={{ width: '100%', p: 0, m: 0 }}>
                      {menuItems
                        .filter(({ path }) => canAccessMenu(path))
                        .map(({ label, path }) => (
                          <CabinetMenuLink
                            key={`cabinetMenu.item.${network}.${label}`}
                            to={`/cabinet/${network.toLowerCase()}${path}`}
                            title={label}
                          />
                        ))}
                    </List>
                  </MenuAccordionDetails>
                </MenuAccordion>
              );
            },
          )}
        </Box>
        <List sx={{ width: '100%', p: 0, m: 0 }}>
          {cabinetBottomMenuRef.current
            .filter(({ path }) => canAccessMenu(path))
            .map(({ label, path }) => (
              <CabinetMenuLink
                key={`cabinetMenu.item.${path}.${label}`}
                to={`/cabinet/facebook${path}`}
                title={label}
              />
            ))}
        </List>
      </Box>
    </Box>
  );
});

CabinetMenu.displayName = 'CabinetMenu';

export default CabinetMenu;
